import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ArrowLeft, Save, Loader2, TestTube } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/contexts/AuthContext';
import { usePushSubscription } from '@/hooks/usePushSubscription';
import { toast } from 'sonner';

const CATEGORY_GROUPS = [
  {
    heading: 'For parents & guardians',
    keys: ['badge_earned', 'level_up', 'daily_task', 'weekly_report', 'homework_alert', 'parent_message', 'newsletter'],
  },
  {
    heading: 'For team & staff',
    keys: ['mention', 'dm', 'meeting_invite', 'meeting_reminder', 'recording_ready'],
  },
  {
    heading: 'System',
    keys: ['system'],
  },
];

export default function NotificationSettingsPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const { subscribe } = usePushSubscription(api, !!user);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/notification-prefs');
        setCategories(res.data.categories || []);
      } catch (err) {
        toast.error('Could not load preferences');
      } finally {
        setLoading(false);
      }
    })();
  }, [api]);

  const toggle = (key, next) => {
    setCategories((prev) => prev.map((c) => (c.key === key ? { ...c, enabled: next } : c)));
  };

  const save = async () => {
    setSaving(true);
    try {
      const prefs = Object.fromEntries(categories.map((c) => [c.key, c.enabled]));
      await api.put('/notification-prefs', { prefs });
      toast.success('Preferences saved');
    } catch (err) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const sub = await subscribe();
      if (!sub.subscribed) {
        toast.error(sub.reason === 'permission-denied' ? 'Enable notifications in your browser settings first' : 'Push not enabled on this device');
        return;
      }
      await api.post('/push/test');
      toast.success('Test push sent — check this device');
    } catch (err) {
      toast.error('Test push failed');
    } finally {
      setTesting(false);
    }
  };

  const grouped = CATEGORY_GROUPS.map((g) => ({
    heading: g.heading,
    items: categories.filter((c) => g.keys.includes(c.key)),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="notification-settings-page">
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="notif-prefs-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-xl font-fredoka font-bold text-slate-800 flex items-center gap-2 truncate">
              <Bell className="w-5 h-5 text-emerald-500" /> Notification Preferences
            </h1>
            <p className="text-xs text-slate-500">Choose which pings this account receives.</p>
          </div>
          <Button variant="outline" size="sm" onClick={sendTest} disabled={testing} data-testid="notif-prefs-test">
            <TestTube className="w-4 h-4 mr-1" /> {testing ? 'Testing…' : 'Test'}
          </Button>
          <Button size="sm" onClick={save} disabled={saving || loading} className="bg-emerald-500 hover:bg-emerald-600" data-testid="notif-prefs-save">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 pb-24 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
          </div>
        ) : (
          grouped.map((group) => (
            <Card key={group.heading}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm uppercase tracking-wide text-slate-500 font-semibold">{group.heading}</CardTitle>
              </CardHeader>
              <CardContent className="divide-y">
                {group.items.map((c) => (
                  <div key={c.key} className="flex items-center justify-between py-3">
                    <div className="min-w-0">
                      <p className="text-sm text-slate-800 font-medium">{c.label}</p>
                      <p className="text-xs text-slate-400">{c.enabled ? 'You will receive these' : 'Silenced on all devices'}</p>
                    </div>
                    <Switch
                      checked={c.enabled}
                      onCheckedChange={(v) => toggle(c.key, v)}
                      data-testid={`toggle-${c.key}`}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          ))
        )}
        <p className="text-xs text-slate-400 text-center px-4">
          These preferences only silence <em>push</em> notifications on this account. Your in-app notification bell will still record every event.
        </p>
      </main>
    </div>
  );
}
